export type StatusOption = { value: string; label: string };

export type RawStatusOptionsMap = Record<string, Record<string, string[]>>;

export type StatusOptionsMap = Record<string, Record<string, StatusOption[]>>;

// 每个服务下按 sub_service 维护状态选项
export const DEFAULT_STATUS_OPTIONS_RAW_MAP: RawStatusOptionsMap = {
  trademark: {
    registration: ['资料审核中', '已提交律师，待发回执', '审查中', '公告中', '被驳回', '被异议驳回', '被无效', '被撤销', '复审中', '已下证（待宣誓/续展）', '公告结束，待提交使用证据', '使用证据已提交，待审查', '审查通过，待下证', '已结案', '非正常程序'],
    transactions: ['资料审核中', '已提交律师，待发回执', '转让中', '被驳回', '已下证（待宣誓/续展）', '已完成(待宣誓/续展)'],
    postpone_evidence: ['资料审核中', '已提交律师，待发回执', '审查中', '被驳回', '已完成', '已结案'],
    transfer: ['资料审核中', '已提交律师，待发回执', '转让中', '被驳回', '已完成(待宣誓/续展)'],
    review: ['资料审核中', '已提交律师，待发回执', '审查中', '被驳回', '已完成', '已结案'],
    revocation: ['资料审核中', '已提交律师，待发回执', '审查中', '被驳回', '已完成', '已结案'],
    search: ['资料审核中', '已提交律师，待发回执', '已完成', '已结案'],
    agency_transfer: ['资料审核中', '已提交律师，待发回执', '转让中', '已完成'],
    certificate_reissue: ['资料审核中', '已提交律师，待发回执', '已完成(待宣誓/续展)'],
    information_changes: ['资料审核中', '已提交律师，待发回执', '审查中', '已完成', '已结案'],
    investment: ['审查中', '已提交律师，待发回执', '公告中', '被驳回', '已下证', '已出售', '已结案'],
    oath: ['资料审核中', '已提交律师，待发回执', '审查中', '被驳回', '已完成(待下次宣誓/续展)'],
    renewal: ['资料审核中', '已提交律师，待发回执', '审查中', '被驳回', '已完成(待下次续展)'],
    cancellation: ['资料审核中', '已提交律师，待发回执', '审查中', '被驳回', '已结案', '已完成'],
    extension: ['资料审核中', '已提交律师，待发回执', '审查中', '被驳回', '已结案', '已完成'],
  },
  patent: {
    appearance_application: ['资料审核中', '已提交律师，待发回执', '审查中', '被驳回', '已通过，待授权', '已授权，待下证', '已下证（待缴年费）', '已结案'],
    invention_application: ['资料审核中', '已提交律师，待发回执', '审查中', '被驳回', '已通过，待授权', '已授权，待下证', '已下证（待缴年费）', '已结案'],
    utility_application: ['资料审核中', '已提交律师，待发回执', '审查中', '被驳回', '已通过，待授权', '已授权，待下证', '已下证（待缴年费）', '已结案'],
    authorization: ['资料审核中', '已提交律师，待发回执', '审查中', '已下证（待缴年费）'],
    rejection_review: ['资料审核中', '已提交律师，待发回执', '审查中', '已完成', '已结案'],
  },
  copyright: {
    registration: ['资料审核中', '已提交律师，待发回执', '审查中', '被驳回', '已下证(待缴纳年费)', '已结案'],
    transfer: ['资料审核中', '已提交律师，待发回执', '转让中', '被驳回', '已完成'],
  },
  company: {
    registration: ['资料审核中', '已提交律师，待发回执', '审查中', '被驳回', '已下证（待年审/续费）', '已完成', '已结案'],
    annual_review: ['资料审核中', '已提交律师，待发回执', '已下证（待年审/续费）', '已完成（待下次年审）'],
    bank_account_opening: ['资料审核中', '已提交律师，待发回执', '已完成(待下次年审)'],
    information_changes: ['资料审核中', '已提交律师，待发回执', '已完成(待下次年审)'],
    cancellation: ['资料审核中', '已提交律师，待发回执', '已完成(待下次年审)'],
    audit: ['资料审核中', '已提交律师，待发回执', '已下证（待年审/续费）', '已完成（待下次年审）'],
  },
  barcode: {
    registration: ['资料审核中', '已提交律师，待发回执', '已下证(待续费)', '已完成(待下次续费)', '已结案'],
    renewal: ['资料审核中', '已提交律师，待发回执', '已完成(待下次续费)'],
    cancellation: ['资料审核中', '已提交律师，待发回执', '已完成'],
    increase: ['资料审核中', '已提交律师，待发回执', '已完成（待续费）'],
  },
  other: {
    registration: ['资料审核中', '已提交律师，待发回执', '审查中', '被驳回', '已完成'],
    cancellation: ['资料审核中', '已提交律师，待发回执', '审查中', '被驳回', '已完成'],
  },
};

export function formatStatusOptionsMap(rawMap: RawStatusOptionsMap): StatusOptionsMap {
  return Object.entries(rawMap).reduce<StatusOptionsMap>((serviceAccumulator, [service, subServices]) => {
    serviceAccumulator[service] = Object.entries(subServices).reduce<Record<string, StatusOption[]>>((subAccumulator, [subService, statuses]) => {
      subAccumulator[subService] = statuses.map((status) => ({
        value: status,
        label: status,
      }));
      return subAccumulator;
    }, {});
    return serviceAccumulator;
  }, {});
}

export const DEFAULT_STATUS_OPTIONS_MAP: StatusOptionsMap = formatStatusOptionsMap(DEFAULT_STATUS_OPTIONS_RAW_MAP);

export function getStatusOptionsBySubService(
  service: string,
  subService?: string,
  statusOptionsMap: StatusOptionsMap = DEFAULT_STATUS_OPTIONS_MAP,
): StatusOption[] {
  const serviceMap = statusOptionsMap[service];

  if (!serviceMap) {
    return [{value: '处理中', label: '处理中'}, {value: '已完成', label: '已完成'}];
  }

  if (subService && serviceMap[subService]) {
    return serviceMap[subService];
  }

  return [];
}
